import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from '../utils/axiosConfig';
import CollapsiblePerformanceFilters from './CollapsiblePerformanceFilters';
import PerformanceCard from './PerformanceCard';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Performance } from '../types/mongodb';
import { isEvent, isBandRecommendation, isStrand } from '../utils/typeGuards';
import type { Strand } from '../types/Strand';
import ScrollToTopButton from './ScrollToTopButton';

// Enhanced Performance type with structured Tito data
interface ExtendedPerformance extends Omit<Performance, 'isTicketed'> {
  isTicketed: boolean; // Make it required, not optional
  titoData?: {
    totalSold?: number;
    totalRevenue?: number;
    status?: 'on_sale' | 'not_on_sale' | 'not_ticketed' | 'error' | 'loading'; // Add 'loading'
    message?: string;
    releases?: Array<{ title: string; quantity: number; tickets_count: number; price: number }>;
  };
}

// Import the exact type that PerformanceCard expects
interface PerformanceCardPerformance extends Omit<Performance, 'isTicketed'> {
  isTicketed: boolean;
  titoData?: {
    totalSold?: number;
    totalRevenue?: number;
    status?: 'on_sale' | 'not_on_sale' | 'not_ticketed' | 'error';
    message?: string;
    releases?: Array<{
      title: string;
      quantity: number;
      tickets_count: number;
      price: number;
    }>;
  };
  venue?: any;
}

interface PerformanceFiltersState {
  event: string;
  band: string;
  strand: string;
  venue: string;
  status: string;
  dateRange: { start: Date | null; end: Date | null };
  timeframe: 'all' | 'past' | 'upcoming' | 'thisMonth' | 'thisYear';
}

const PerformanceList: React.FC = () => {
  // States
  const [performances, setPerformances] = useState<ExtendedPerformance[]>([]);
  const [filteredPerformances, setFilteredPerformances] = useState<ExtendedPerformance[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [bands, setBands] = useState<string[]>([]);
  const [strands, setStrands] = useState<Strand[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedPerformance, setSelectedPerformance] = useState<ExtendedPerformance | null>(null);
  const [archiveReason, setArchiveReason] = useState<'pastEvent' | 'cancelled' | 'redundant' | 'other'>('pastEvent');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [filters, setFilters] = useState<PerformanceFiltersState>({
    event: '',
    band: '',
    strand: '',
    venue: '',
    status: '',
    dateRange: { start: null, end: null },
    timeframe: 'upcoming'
  });
  const [ticketDataQueue, setTicketDataQueue] = useState<string[]>([]);
  const [loadingTicketData, setLoadingTicketData] = useState<Set<string>>(new Set());

  // Helper functions
  const getEventName = (event: any): string => (event && isEvent(event)) ? event.name : 'Not assigned to event';
  const getBandName = (band: any, simpleBandName?: string): string => simpleBandName || (band && isBandRecommendation(band) ? band.artistName : 'Unknown Artist');
  const getVenueId = (event: any): string | undefined => isEvent(event) && event.venue ? (typeof event.venue === 'string' ? event.venue : event.venue._id) : undefined;

  // Generate Tito slug
  const generateTitoSlug = (performance: Performance): string => {
    const artistName = performance.simpleBandName ||
      (typeof performance.band === 'object' && performance.band && 'artistName' in performance.band ? performance.band.artistName : '');
    let venueName = 'cooler';
    if (performance.event && typeof performance.event === 'object' && performance.event.venue && typeof performance.event.venue === 'object') {
      venueName = performance.event.venue.name?.toLowerCase() || 'cooler';
    }
    const date = new Date(performance.startTime);
    const month = date.toLocaleString('en-US', { month: 'short' }).toLowerCase();
    const year = date.getFullYear().toString().slice(-2);
    const cleanText = (text: string) => text.toLowerCase()
      .replace(/^the\s+/i, '')
      .replace(/\s+the\s+/g, ' ')
      .replace(/['']/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const cleanArtist = cleanText(artistName);
    const cleanVenue = cleanText(venueName);
    return `${cleanArtist}-${cleanVenue}-${month}${year}`;
  };

// Fetch Tito data safely
const fetchTitoData = async (performance: Performance): Promise<ExtendedPerformance> => {
  // If the performance is marked as not ticketed, don't fetch Tito data
  if (performance.isTicketed === false) {
    return { 
      ...performance, 
      titoData: { 
        status: 'not_ticketed', 
        message: 'Event is not ticketed', 
        totalSold: 0, 
        totalRevenue: 0 
      } 
    };
  }

  try {
    const slug = generateTitoSlug(performance);
    try {
      const eventResp = await axiosInstance.get(`/api/tito/events/${slug}`);
      if (eventResp.data?.event) {
        const [regs, rels] = await Promise.all([
          axiosInstance.get(`/api/tito/${slug}/registrations`),
          axiosInstance.get(`/api/tito/${slug}/releases`)
        ]);
        const releases = rels.data?.releases || [];
        const totalSold = releases.reduce((sum: number, r: any) => sum + (parseInt(r.tickets_count) || 0), 0);
        const totalRevenue = releases.reduce((sum: number, r: any) => sum + (parseInt(r.tickets_count) || 0) * (parseFloat(r.price) || 0), 0);
        return { ...performance, titoData: { status: 'on_sale', totalSold, totalRevenue, releases } };
      } else {
        return { ...performance, titoData: { status: 'not_on_sale', message: 'Event exists but no data', totalSold: 0, totalRevenue: 0 } };
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        return { ...performance, titoData: { status: 'not_on_sale', message: 'Not on sale', totalSold: 0, totalRevenue: 0 } };
      }
      return { ...performance, titoData: { status: 'error', message: err.message || 'Error', totalSold: 0, totalRevenue: 0 } };
    }
  } catch (err: any) {
    return { ...performance, titoData: { status: 'error', message: err.message || 'Error', totalSold: 0, totalRevenue: 0 } };
  }
};

// Smart ticket data loading
const loadTicketDataForPerformance = async (performanceId: string) => {
  setLoadingTicketData(prev => new Set(prev).add(performanceId));
  
  const performance = performances.find(p => p._id === performanceId);
  if (!performance) return;

  try {
    const updatedPerformance = await fetchTitoData(performance);
    setPerformances(prev => 
      prev.map(p => p._id === performanceId ? updatedPerformance : p)
    );
  } catch (error) {
    console.error(`Failed to load ticket data for ${performanceId}:`, error);
  } finally {
    setLoadingTicketData(prev => {
      const newSet = new Set(prev);
      newSet.delete(performanceId);
      return newSet;
    });
  }
};

// Process ticket data queue
useEffect(() => {
  if (ticketDataQueue.length === 0) return;

  const processQueue = async () => {
    const batch = ticketDataQueue.slice(0, 3); // Process 3 at a time
    const remaining = ticketDataQueue.slice(3);
    
    setTicketDataQueue(remaining);
    
    await Promise.all(batch.map(loadTicketDataForPerformance));
    
    if (remaining.length > 0) {
      // Small delay between batches to prevent overwhelming the API
      setTimeout(() => setTicketDataQueue(remaining), 500);
    }
  };

  processQueue();
}, [ticketDataQueue, performances]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [perfRes, strandsRes, venuesRes] = await Promise.all([
          axiosInstance.get('/api/performances'),
          axiosInstance.get('/api/strands'),
          axiosInstance.get('/api/venues')
        ]);
        const raw: Performance[] = perfRes.data;
        
        // Initialize performances with loading status for ticket data
        const initialized: ExtendedPerformance[] = raw.map(p => ({
          ...p,
          titoData: p.isTicketed === false 
            ? { status: 'not_ticketed', message: 'Event is not ticketed', totalSold: 0, totalRevenue: 0 }
            : { status: 'loading', message: 'Loading ticket data...', totalSold: 0, totalRevenue: 0 }
        }));

        setPerformances(initialized);
        setStrands(strandsRes.data);
        setVenues(venuesRes.data);
        setEvents(Array.from(new Set(initialized.map(p => getEventName(p.event)))).filter(n => n !== 'Not assigned to event'));
        setBands(Array.from(new Set(initialized.map(p => getBandName(p.band, p.simpleBandName)))).filter(n => n !== 'Unknown Artist'));
        
        // Queue up ticket data loading for ticketed performances
        const ticketedPerformanceIds = initialized
          .filter(p => p.isTicketed !== false)
          .map(p => p._id);
        setTicketDataQueue(ticketedPerformanceIds);
        
      } catch (err) {
        console.error(err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    const now = new Date();
    let temp: ExtendedPerformance[] = [];

    // First filter by tab (archive status + time)
    switch (tabValue) {
      case 0: // Upcoming (current logic - non-archived + upcoming)
        temp = performances.filter(p => !p.isArchived && new Date(p.startTime) > now);
        break;
      case 1: // Past (non-archived + past)
        temp = performances.filter(p => !p.isArchived && new Date(p.endTime) < now);
        break;
      case 2: // Archived
        temp = performances.filter(p => p.isArchived);
        break;
      default:
        temp = performances;
    }

    // Apply additional filters only for Past tab (tabValue === 1)
    if (tabValue === 1) {
      // Apply timeframe filter for Past tab
      switch (filters.timeframe) {
        case 'thisMonth': 
          temp = temp.filter(p => { 
            const d = new Date(p.startTime); 
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); 
          }); 
          break;
        case 'thisYear': 
          temp = temp.filter(p => new Date(p.startTime).getFullYear() === now.getFullYear()); 
          break;
        // For Past tab, 'past' and 'all' show all past performances
        // 'upcoming' doesn't make sense for past tab, so treat it as 'all'
      }

      // Apply date range filters
      if (filters.dateRange.start) temp = temp.filter(p => new Date(p.startTime) >= filters.dateRange.start!);
      if (filters.dateRange.end) temp = temp.filter(p => new Date(p.startTime) <= filters.dateRange.end!);
      
      // Apply other filters
      if (filters.event) temp = temp.filter(p => getEventName(p.event) === filters.event);
      if (filters.band) temp = temp.filter(p => getBandName(p.band, p.simpleBandName) === filters.band);
      if (filters.strand) temp = temp.filter(p => p.strandId && (isStrand(p.strandId) ? p.strandId._id === filters.strand : p.strandId === filters.strand));
      if (filters.venue) temp = temp.filter(p => getVenueId(p.event) === filters.venue);
      if (filters.status) temp = temp.filter(p => p.status === filters.status);
    }

    temp.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    setFilteredPerformances(temp);

    // Update active filters display (only show for Past tab)
    const tags: string[] = [];
    if (tabValue === 1) {
      if (filters.event) tags.push(`Event: ${filters.event}`);
      if (filters.band) tags.push(`Band: ${filters.band}`);
      if (filters.strand) { 
        const n = strands.find(s => s._id === filters.strand)?.name; 
        if (n) tags.push(`Strand: ${n}`); 
      }
      if (filters.venue) { 
        const v = venues.find(vn => vn._id === filters.venue)?.name; 
        if (v) tags.push(`Venue: ${v}`); 
      }
      if (filters.status) tags.push(`Status: ${filters.status}`);
      if (filters.timeframe !== 'all') tags.push(`Time: ${filters.timeframe}`);
    }
    setActiveFilters(tags);
  }, [performances, filters, tabValue, strands, venues]);

  // Handlers
  const handleFilterChange = (name: string, value: any) => setFilters(prev => ({ ...prev, [name]: value }));
  const handleClearFilter = (text: string) => { 
    const key = text.split(':')[0].toLowerCase(); 
    setFilters(prev => ({ ...prev, [key]: '' })); 
  };
  const handleArchive = (perf: ExtendedPerformance) => { 
    setSelectedPerformance(perf); 
    setArchiveDialogOpen(true); 
  };
  const handleConfirmArchive = async () => {
    if (!selectedPerformance) return;
    try {
      await axiosInstance.post(`/api/performances/${selectedPerformance._id}/archive`, { 
        reason: archiveReason, 
        notes: archiveNotes 
      });
      setPerformances(prev => prev.map(p => p._id === selectedPerformance._id ? 
        { ...p, isArchived: true, archiveReason, archiveNotes } : p
      ));
      setArchiveDialogOpen(false);
    } catch { 
      setError('Failed to archive'); 
    }
  };
  const handleUnarchive = (perf: ExtendedPerformance) => {
    axiosInstance.post(`/api/performances/${perf._id}/unarchive`)
      .then(() => setPerformances(prev => prev.map(p => p._id === perf._id ? 
        { ...p, isArchived: false, archiveReason: undefined, archiveNotes: undefined } : p
      )))
      .catch(() => setError('Failed to unarchive'));
  };

  // Group by month
  const grouped = useMemo(() => filteredPerformances.reduce((acc, p) => { 
    const d = new Date(p.startTime); 
    const key = `${d.getFullYear()}-${d.getMonth()}`; 
    (acc[key] || (acc[key] = [])).push(p); 
    return acc; 
  }, {} as Record<string, ExtendedPerformance[]>), [filteredPerformances]);
  
const monthKeys = useMemo(() => {
  const keys = Object.keys(grouped).sort((a, b) => {
    const [ya, ma] = a.split('-').map(Number);
    const [yb, mb] = b.split('-').map(Number);
    return new Date(ya, ma).getTime() - new Date(yb, mb).getTime();
  });
  return tabValue === 1 ? keys.reverse() : keys;
}, [grouped, tabValue]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>Performances</Typography>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading...' : `${filteredPerformances.length} ${
                tabValue === 0 ? 'upcoming' : 
                tabValue === 1 ? 'past' : 
                'archived'
              } performances`}
            </Typography>
          </Box>
          <Button component={Link} to="/admin/performances/new" variant="contained" color="primary">
            Create New Performance
          </Button>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Upcoming" />
            <Tab label="Past" />
            <Tab label="Archived" />
          </Tabs>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        
        {/* Only show filters for Past tab */}
        {tabValue === 1 && (
          <CollapsiblePerformanceFilters 
            events={events} 
            bands={bands} 
            strands={strands} 
            venues={venues} 
            filters={filters} 
            onFilterChange={handleFilterChange} 
            activeFilters={activeFilters} 
            onClearFilter={handleClearFilter} 
          />
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : monthKeys.length ? (
          monthKeys.map(key => {
            const [year, month] = key.split('-').map(Number);
            const list = grouped[key];
            const label = new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });
            return (
              <Accordion key={key} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    {/* Month name in yellow, larger font */}
                    <Typography variant="h5" sx={{ color: 'yellow' }}>
                      {label}
                    </Typography>
                    {/* Count in green, smaller font */}
                    <Typography variant="subtitle2" sx={{ color: '#ADD8E6', ml: 1 }}>
                      ({list.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    {list.map(p => {
                      // Convert ExtendedPerformance to PerformanceCard compatible type
                      const performanceForCard: PerformanceCardPerformance = {
                        ...p,
                        titoData: p.titoData?.status === 'loading' 
                          ? { ...p.titoData, status: 'error', message: 'Loading ticket data...' }
                          : p.titoData as PerformanceCardPerformance['titoData']
                      };
                      return (
                        <Grid item xs={12} sm={6} md={4} key={p._id}>
                          <PerformanceCard 
                            performance={performanceForCard} 
                            onArchive={handleArchive} 
                            onUnarchive={handleUnarchive} 
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="h6">No performances found</Typography>
              <Typography>
                {activeFilters.length ? 'Try adjusting your filters.' : 'There are no performances yet.'}
              </Typography>
            </Alert>
          </Grid>
        )}
        
        <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>
            Archive Performance: {selectedPerformance?.simpleBandName || getBandName(selectedPerformance?.band, selectedPerformance?.simpleBandName)}
          </DialogTitle>
          <DialogContent>
            <TextField 
              select 
              fullWidth 
              label="Archive Reason" 
              value={archiveReason} 
              onChange={e => setArchiveReason(e.target.value as any)} 
              margin="normal"
            > 
              <MenuItem value="pastEvent">Past Event</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="redundant">Redundant</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Archive Notes" 
              value={archiveNotes} 
              onChange={e => setArchiveNotes(e.target.value)} 
              margin="normal" 
              multiline 
              rows={3} 
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmArchive} variant="contained" color="primary">Archive</Button>
          </DialogActions>
        </Dialog>
        
        <ScrollToTopButton backgroundColor="#1976d2" color="#ffffff" scrollThreshold={500} size={50} />
      </Container>
    </LocalizationProvider>
  );
};

export default PerformanceList;